import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuthStore } from '../../../stores/authStore';
import type { Announcement, AnnouncementScope } from '../types';

// Admin parity (C2·P5). Announcements — read the business's announcement messages
// and send new ones via the canonical send_message RPC, which fans out into
// message_recipients for the chosen scope. RLS lets the sending operator read
// back their own + 'all'-scoped announcements; recipient rows are NOT readable
// per-row (own-receipt RLS), so the list shows the scope, not a head-count.

export function useAnnouncements() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const businessId = business?.id;
  const currentUserId = user?.id;
  const mountedRef = useRef(true);

  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchAnnouncements = useCallback(async () => {
    if (!businessId || !currentUserId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error: err } = await supabase
        .from('messages')
        .select('id, business_id, sender_id, content, announcement_scope, created_at, deleted_at, sender:users!sender_id(first_name, last_name)')
        .eq('business_id', businessId)
        .eq('message_type', 'announcement')
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(50);
      if (err) throw err;
      if (!mountedRef.current) return;
      const list: Announcement[] = (data ?? []).map((m: Record<string, unknown>) => ({
        id: m.id as string,
        business_id: m.business_id as string,
        sender_id: m.sender_id as string,
        content: m.content as string,
        announcement_scope: (m.announcement_scope as Announcement['announcement_scope']) ?? null,
        created_at: m.created_at as string,
        deleted_at: (m.deleted_at as string | null) ?? null,
        senderName: m.sender
          ? `${(m.sender as Record<string, string>).first_name} ${(m.sender as Record<string, string>).last_name}`
          : 'Unknown',
        recipientCount: null,
      }));
      setAnnouncements(list);
    } catch { /* silent */ }
    finally { if (mountedRef.current) setLoading(false); }
  }, [businessId, currentUserId]);

  useEffect(() => { void fetchAnnouncements(); }, [fetchAnnouncements]);

  // Realtime: refetch on any announcement insert/update for the business.
  useEffect(() => {
    if (!businessId) return;
    const ch = supabase
      .channel(`admin-announcements-${businessId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'messages', filter: `business_id=eq.${businessId}` }, () => { void fetchAnnouncements(); })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [businessId, fetchAnnouncements]);

  const sendAnnouncement = useCallback(async (
    content: string,
    scope: AnnouncementScope,
  ): Promise<{ success: boolean; error?: string }> => {
    if (!businessId || !currentUserId || !content.trim()) {
      return { success: false, error: 'Not authenticated or empty message' };
    }
    setSending(true);
    try {
      // Canonical send path: insert + message_recipients fan-out for the scope, atomic.
      const { data, error: err } = await supabase.rpc('send_message', {
        p_message_type: 'announcement',
        p_content: content.trim(),
        p_announcement_scope: scope,
      });
      if (err) return { success: false, error: err.message };
      if (!data?.success) return { success: false, error: data?.error?.message ?? 'Failed to send' };
      await fetchAnnouncements();
      return { success: true };
    } catch (e) {
      return { success: false, error: e instanceof Error ? e.message : 'Failed to send' };
    } finally {
      if (mountedRef.current) setSending(false);
    }
  }, [businessId, currentUserId, fetchAnnouncements]);

  return { announcements, loading, sending, sendAnnouncement, refresh: fetchAnnouncements };
}
